import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, Bot, Briefcase, CalendarClock, ChevronDown, ChevronUp, CircleDollarSign,
  Clock, Cpu, Edit3, Focus, HeartPulse, Inbox, MessageSquare, Plus, RefreshCw,
  ShieldAlert, Sparkles, Target, Trash2, TrendingUp, Trophy, X,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/authStore";
import { useToasts } from "../components/Toasts";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import Modal from "../components/Modal";
import { fmtDateTime, relTime } from "../lib/format";

const PRIMARY_QUESTIONS = [
  "What needs me now?",
  "What am I waiting on?",
  "What is at risk?",
  "What can AI handle without me?",
  "Where should I spend my limited time?",
];

const BUCKETS = ["NOW", "NEXT", "PARKING_LOT"];
const BUCKET_LABEL = { NOW: "Now", NEXT: "Next", PARKING_LOT: "Parking Lot" };
const ROLES = ["Founder", "Family", "Employee", "Personal"];
const KINDS = [
  { id: "needs_you", label: "Needs You" },
  { id: "waiting_on", label: "Waiting On" },
  { id: "at_risk", label: "At Risk" },
  { id: "ai_can_handle", label: "AI Can Handle" },
  { id: "upcoming", label: "Upcoming" },
  { id: "general", label: "General" },
];
const KIND_TONE = { needs_you: "amber", waiting_on: "dim", at_risk: "red", ai_can_handle: "blue", upcoming: "purple", general: "dim" };
const HEALTH_TONE = { AT_RISK: "red", WATCH: "amber", BUILDING: "blue", HEALTHY: "green", UNKNOWN: "dim" };

function LincName({ name }) {
  if (!name) return null;
  const idx = name.toLowerCase().lastIndexOf("linc");
  if (idx === -1) return <>{name}</>;
  return <>{name.slice(0, idx)}<span className="linc">{name.slice(idx, idx + 4)}</span>{name.slice(idx + 4)}</>;
}

function TileHeader({ title, Icon, note, action }) {
  return (
    <div className="panel-head">
      <div><h2 style={{ display: "flex", alignItems: "center", gap: 8 }}><Icon size={14} /> {title}</h2>{note ? <p>{note}</p> : null}</div>
      {action}
    </div>
  );
}

function NotConnected({ children }) {
  return (
    <div className="hint" style={{ padding: "16px 4px", fontSize: 12 }}>
      <span className="pill amber" style={{ marginRight: 6 }}>NOT YET CONNECTED</span>{children}
    </div>
  );
}

const LS_ROLE_KEY = "lw.focus.role";
const LS_KINDS_KEY = "lw.focus.kinds";

// -------- Item card --------
function FocusCard({ item, businesses, onEdit, onMove, onStatus, onDelete, onPriority }) {
  const auto = item.source !== "manual";
  const kindLabel = KINDS.find((k) => k.id === item.kind)?.label || item.kind;
  const sourceLink = item.source_ref?.mission_id
    ? `/commandlinc/missions/${item.source_ref.mission_id}`
    : item.source_ref?.business_key
      ? `/businesses/${item.source_ref.business_key}`
      : null;
  return (
    <div className="focus-item" data-testid={`focus-item-${item.id}`}>
      <div className="focus-item-body">
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span className={`pill ${KIND_TONE[item.kind] || "dim"}`}>{kindLabel}</span>
          {auto ? <span className="pill dim" title={`auto-sourced from ${item.source}`} data-testid={`auto-badge-${item.id}`}>AUTO</span> : null}
          {item.role ? <span className="pill dim">{item.role}</span> : null}
          {item.business_key ? <span className="pill dim"><LincName name={item.business_key} /></span> : null}
        </div>
        <b style={{ display: "block", marginTop: 6, textDecoration: item.status === "done" ? "line-through" : "none", opacity: item.status === "done" ? 0.55 : 1 }}>{item.title}</b>
        {item.notes ? <p className="hint" style={{ fontSize: 11, marginTop: 4 }}>{item.notes}</p> : null}
        <p className="hint" style={{ fontSize: 10, marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span>urgency <b>{item.urgency_score}</b></span>
          <span>risk <b>{item.risk_score}</b></span>
          {item.deadline ? <span>· deadline {fmtDateTime(item.deadline)}</span> : null}
          {item.due_date ? <span>· due {item.due_date}</span> : null}
          <span>· #{item.priority}</span>
          {sourceLink ? <Link to={sourceLink} onClick={(e) => e.stopPropagation()}>· view source</Link> : null}
        </p>
      </div>
      <div className="focus-item-actions">
        <button className="icon-btn" onClick={() => onPriority(item, -1)} title="Higher priority"><ChevronUp size={12} /></button>
        <button className="icon-btn" onClick={() => onPriority(item, 1)} title="Lower priority"><ChevronDown size={12} /></button>
        <select value={item.bucket} onChange={(e) => onMove(item, e.target.value)} className="btn small" title="Move" data-testid={`focus-move-${item.id}`}>
          {BUCKETS.map((b) => <option key={b} value={b}>{BUCKET_LABEL[b]}</option>)}
        </select>
        <button className="icon-btn" title="Edit" onClick={() => onEdit(item, businesses)} data-testid={`focus-edit-${item.id}`}><Edit3 size={12} /></button>
        <select
          value=""
          onChange={(e) => onStatus(item, e.target.value)}
          className="btn small"
          title="Status"
          data-testid={`focus-status-${item.id}`}
        >
          <option value="">…</option>
          <option value="done">Mark done</option>
          <option value="snoozed">Snooze…</option>
          <option value="archived">Archive…</option>
          <option value="open">Reopen</option>
        </select>
        <button className="icon-btn" title="Delete" onClick={() => onDelete(item)} data-testid={`focus-delete-${item.id}`}><Trash2 size={12} /></button>
      </div>
    </div>
  );
}

// -------- Create/Edit modal --------
function ItemForm({ initial, businesses, onClose, onSubmit, mode }) {
  const [form, setForm] = useState(() => ({
    title: initial?.title || "",
    notes: initial?.notes || "",
    bucket: initial?.bucket || "NOW",
    kind: initial?.kind || "general",
    role: initial?.role || "",
    business_key: initial?.business_key || "",
    mission_id: initial?.mission_id || "",
    due_date: initial?.due_date || "",
    deadline: initial?.deadline ? initial.deadline.slice(0, 16) : "",
    urgency_score: initial?.urgency_score ?? 50,
    risk_score: initial?.risk_score ?? 50,
  }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = () => {
    if (!form.title.trim()) return;
    const payload = { ...form };
    payload.role = payload.role || null;
    payload.business_key = payload.business_key || null;
    payload.mission_id = payload.mission_id || null;
    payload.due_date = payload.due_date || null;
    payload.deadline = payload.deadline ? new Date(payload.deadline).toISOString() : null;
    onSubmit(payload);
  };
  return (
    <Modal title={mode === "edit" ? "Edit focus item" : "New focus item"} eyebrow="FOCUSLINC" onClose={onClose} testId="focus-item-modal">
      <label><small className="hint">Title *</small>
        <input autoFocus value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="fi-title" style={{ width: "100%", marginTop: 4 }} />
      </label>
      <label style={{ display: "block", marginTop: 10 }}>
        <small className="hint">Notes</small>
        <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} style={{ width: "100%", marginTop: 4 }} />
      </label>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 10 }}>
        <label><small className="hint">Bucket</small>
          <select value={form.bucket} onChange={(e) => set("bucket", e.target.value)} className="btn small" style={{ width: "100%", marginTop: 4 }}>
            {BUCKETS.map((b) => <option key={b} value={b}>{BUCKET_LABEL[b]}</option>)}
          </select>
        </label>
        <label><small className="hint">Kind</small>
          <select value={form.kind} onChange={(e) => set("kind", e.target.value)} className="btn small" style={{ width: "100%", marginTop: 4 }}>
            {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
        </label>
        <label><small className="hint">Role</small>
          <select value={form.role} onChange={(e) => set("role", e.target.value)} className="btn small" style={{ width: "100%", marginTop: 4 }}>
            <option value="">— none —</option>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
        <label><small className="hint">Business</small>
          <select value={form.business_key} onChange={(e) => set("business_key", e.target.value)} className="btn small" style={{ width: "100%", marginTop: 4 }}>
            <option value="">— none —</option>
            {businesses.map((b) => <option key={b.key || b.slug} value={b.key || b.slug}>{b.name}</option>)}
          </select>
        </label>
        <label><small className="hint">Mission id (optional)</small>
          <input value={form.mission_id} onChange={(e) => set("mission_id", e.target.value)} placeholder="mission uuid" style={{ width: "100%", marginTop: 4 }} />
        </label>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
        <label><small className="hint">Due date</small>
          <input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} style={{ width: "100%", marginTop: 4 }} />
        </label>
        <label><small className="hint">Hard deadline</small>
          <input type="datetime-local" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} style={{ width: "100%", marginTop: 4 }} />
        </label>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
        <label><small className="hint">Urgency <b>{form.urgency_score}</b></small>
          <input type="range" min="0" max="100" value={form.urgency_score} onChange={(e) => set("urgency_score", parseInt(e.target.value, 10))} style={{ width: "100%" }} />
        </label>
        <label><small className="hint">Risk <b>{form.risk_score}</b></small>
          <input type="range" min="0" max="100" value={form.risk_score} onChange={(e) => set("risk_score", parseInt(e.target.value, 10))} style={{ width: "100%" }} />
        </label>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" onClick={submit} disabled={!form.title.trim()} data-testid="fi-submit">
          {mode === "edit" ? "Save" : "Create"}
        </button>
      </div>
    </Modal>
  );
}

function ReasonModal({ title, prompt, onClose, onSubmit, testId }) {
  const [reason, setReason] = useState("");
  return (
    <Modal title={title} eyebrow="CONFIRM" onClose={onClose} testId={testId}>
      <p className="hint" style={{ marginTop: 6 }}>{prompt}</p>
      <textarea
        autoFocus rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Why?"
        style={{ width: "100%", marginTop: 10 }} data-testid="reason-input"
      />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={reason.trim().length < 3} onClick={() => onSubmit(reason.trim())} data-testid="reason-submit">
          Confirm
        </button>
      </div>
    </Modal>
  );
}

export default function FocusPage() {
  const { user } = useAuth();
  const pushToast = useToasts((s) => s.push);

  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [top3, setTop3] = useState(null);
  const [roleFilter, setRoleFilter] = useState(() => localStorage.getItem(LS_ROLE_KEY) || "ALL");
  const [kindFilter, setKindFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KINDS_KEY) || "[]"); } catch { return []; }
  });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingMove, setPendingMove] = useState(null); // { item, to }
  const [pendingStatus, setPendingStatus] = useState(null); // { item, to }

  const load = useCallback(async () => {
    const [{ data: s }, { data: fi }, { data: bz }, { data: t3 }] = await Promise.all([
      api.get("/focus/summary"),
      api.get("/focus_items?limit=300"),
      api.get("/businesses"),
      api.get("/focus/top3"),
    ]);
    setSummary(s); setItems(fi); setBusinesses(bz); setTop3(t3);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { localStorage.setItem(LS_ROLE_KEY, roleFilter); }, [roleFilter]);
  useEffect(() => { localStorage.setItem(LS_KINDS_KEY, JSON.stringify(kindFilter)); }, [kindFilter]);

  const filtered = useMemo(() => items.filter((i) => {
    if (i.status !== "open") return false;
    if (roleFilter !== "ALL" && i.role !== roleFilter) return false;
    if (kindFilter.length && !kindFilter.includes(i.kind)) return false;
    return true;
  }), [items, roleFilter, kindFilter]);

  const byBucket = useMemo(() => {
    const g = { NOW: [], NEXT: [], PARKING_LOT: [] };
    for (const i of filtered) g[i.bucket]?.push(i);
    for (const k of BUCKETS) g[k].sort((a, b) => a.priority - b.priority);
    return g;
  }, [filtered]);

  // --------- actions ---------
  const doCreate = async (payload) => {
    await api.post("/focus_items", payload);
    setCreating(false);
    pushToast("Focus item created.", "ok");
    load();
  };
  const doEditSave = async (payload) => {
    await api.patch(`/focus_items/${editing.id}`, payload);
    setEditing(null);
    pushToast("Focus item updated.", "ok");
    load();
  };
  const requestMove = (item, to) => {
    if (item.bucket === to) return;
    if (item.bucket === "NOW" && to !== "NOW") {
      setPendingMove({ item, to });
      return;
    }
    doMove(item, to, null);
  };
  const doMove = async (item, to, reason) => {
    const from = item.bucket;
    await api.post(`/focus_items/${item.id}/move`, { to_bucket: to, reason });
    pushToast(`Moved to ${BUCKET_LABEL[to]}.`, "ok", {
      duration: 10000,
      action: {
        label: "Undo",
        onClick: async () => {
          await api.post(`/focus_items/${item.id}/move`, { to_bucket: from, reason: "undo" });
          load();
        },
      },
    });
    setPendingMove(null);
    load();
  };
  const requestStatus = (item, to) => {
    if (!to) return;
    if (to === "done" || to === "open") { doStatus(item, to, null, null); return; }
    setPendingStatus({ item, to });
  };
  const doStatus = async (item, to, reason, snoozed_until) => {
    const body = { status: to };
    if (reason) body.reason = reason;
    if (snoozed_until) body.snoozed_until = snoozed_until;
    try {
      await api.post(`/focus_items/${item.id}/status`, body);
      pushToast(`Marked ${to}.`, "ok");
      setPendingStatus(null);
      load();
    } catch (e) {
      pushToast(e?.response?.data?.detail || "Status change failed.", "err");
    }
  };
  const doDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This archives it.`)) return;
    try {
      await api.delete(`/focus_items/${item.id}`);
      pushToast("Focus item archived.", "ok");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      pushToast(typeof detail === "string" ? detail : (detail?.message || "Delete blocked."), "err");
    }
  };
  const doPriority = async (item, delta) => {
    await api.patch(`/focus_items/${item.id}`, { priority: (item.priority || 0) + delta });
    load();
  };

  const runSync = async () => {
    try {
      const { data } = await api.post("/focus/sync");
      pushToast(`Sync: +${data.created} ✱ ${data.updated} ✔ ${data.closed}`, "ok");
      load();
    } catch { pushToast("Sync failed.", "err"); }
  };

  if (!summary) return <TopBar title="FocusLinc" subtitle="Loading…" />;

  const c = summary.cross_business_summary || {};
  const cs = summary.cost_summary || {};
  const sh = summary.system_health || {};
  const atRiskBiz = summary.at_risk?.businesses || [];
  const atRiskMissions = summary.at_risk?.missions || [];

  const toggleKind = (k) => {
    setKindFilter((cur) => cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
  };

  return (
    <div data-edit-name="FocusLinc classic">
      <TopBar
        title={<><span className="linc">Focus</span>Linc</>}
        subtitle={`Where ${user?.name || "you"} spend limited time.`}
        actions={
          <>
            <button className="btn small" onClick={runSync} data-testid="focus-sync-btn"><RefreshCw size={12} /> Sync</button>
            <button className="btn small primary" onClick={() => setCreating(true)} data-testid="focus-new-btn"><Plus size={12} /> New item</button>
          </>
        }
      />

      <div className="focus-questions" data-testid="focus-primary-questions" data-edit-name="FocusLinc questions">
        {PRIMARY_QUESTIONS.map((q) => <span key={q} className="focus-question"><Sparkles size={11} /> {q}</span>)}
      </div>

      {/* Today's Top 3 */}
      <div className="panel" data-testid="focus-top3-panel" data-edit-name="Today's Top 3" style={{ marginBottom: 14 }}>
        <div className="panel-head">
          <div>
            <h2><Trophy size={14} style={{ verticalAlign: -2 }} /> Today&apos;s Top 3</h2>
            <p>Ranked by the Attention Engine · <span className="mono">algorithm v1</span>.</p>
          </div>
          <button className="btn small" onClick={load} data-testid="top3-refresh"><RefreshCw size={11} /> Refresh</button>
        </div>
        <div className="panel-body">
          {(!top3 || top3.top_3.length === 0)
            ? <EmptyState title="No items in NOW yet — add one below to start prioritizing." icon={Target} />
            : (
              <div className="top3-grid">
                {top3.top_3.map((r, i) => (
                  <div className="top3-card" key={r.item.id} data-testid={`top3-rank-${i + 1}`}>
                    <div className="top3-rank">
                      <span className="top3-num">#{i + 1}</span>
                      <span className="pill dim mono" data-testid={`top3-score-${i + 1}`}>score {r.score}</span>
                    </div>
                    <b className="top3-title">{r.item.title}</b>
                    <ul className="top3-reasons" data-testid={`top3-reasons-${i + 1}`}>
                      {r.reasons.map((rr, ii) => <li key={ii}><Cpu size={10} /> {rr}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            )
          }
        </div>
      </div>

      {/* Tiles at top */}
      <div className="focus-grid focus-row-top">
        <div className="panel" data-testid="tile-needs-you">
          <TileHeader title="Needs You" Icon={Inbox} note="Director approvals waiting."
            action={<Link to="/commandlinc/approvals" className="btn small">Open</Link>} />
          <div className="panel-body">
            {(summary.needs_you || []).length === 0
              ? <EmptyState title="Inbox clear." />
              : summary.needs_you.slice(0, 5).map((a) => (
                <Link to={`/commandlinc/missions/${a.mission_id}`} key={a.approval_id} className="wide-row row-button" style={{ width: "100%" }}>
                  <div><b>{a.mission_title || "(untitled)"}</b>
                    <p className="hint" style={{ fontSize: 11 }}><span className="mono">{a.stage_key}</span> · {a.kind} · {relTime(a.requested_at)}</p>
                  </div>
                  <span className="pill amber">pending</span>
                </Link>
              ))
            }
          </div>
        </div>

        <div className="panel" data-testid="tile-at-risk">
          <TileHeader title="At Risk" Icon={ShieldAlert} />
          <div className="panel-body">
            {atRiskBiz.length === 0 && atRiskMissions.length === 0
              ? <EmptyState title="Nothing at risk." />
              : (<>
                {atRiskBiz.slice(0, 5).map((b) => (
                  <Link to={`/businesses/${b.key}`} key={"b-" + b.key} className="wide-row row-button" style={{ width: "100%" }}>
                    <div><b><LincName name={b.name} /></b>{b.target_date ? <p className="hint" style={{ fontSize: 11 }}>target {b.target_date}</p> : null}</div>
                    <span className={`pill ${HEALTH_TONE[b.health] || "dim"}`}>{b.health}</span>
                  </Link>
                ))}
                {atRiskMissions.slice(0, 5).map((m) => (
                  <Link to={`/commandlinc/missions/${m.id}`} key={"m-" + m.id} className="wide-row row-button" style={{ width: "100%" }}>
                    <div><b>{m.title}</b><p className="hint" style={{ fontSize: 11 }}>stage <span className="mono">{m.current_stage}</span></p></div>
                    <span className="pill red">blocked</span>
                  </Link>
                ))}
              </>)
            }
          </div>
        </div>

        <div className="panel" data-testid="tile-cross-business">
          <TileHeader title="Cross-business" Icon={TrendingUp} />
          <div className="panel-body">
            <div className="wide-row"><div><b>In progress</b></div><span className="pill blue">{c.in_progress ?? 0}</span></div>
            <div className="wide-row"><div><b>Waiting review</b></div><span className="pill amber">{c.waiting_review ?? 0}</span></div>
            <div className="wide-row"><div><b>Blocked</b></div><span className="pill red">{c.blocked ?? 0}</span></div>
            <div className="wide-row"><div><b>Completed 7d</b></div><span className="pill green">{c.completed_last_7d ?? 0}</span></div>
          </div>
        </div>
      </div>

      <div className="focus-grid focus-row-mid" style={{ marginTop: 14 }}>
        <div className="panel" data-testid="tile-ai-can-handle">
          <TileHeader title="AI can handle" Icon={Bot} />
          <div className="panel-body">
            {(summary.ai_can_handle || []).length === 0
              ? <EmptyState title="No AI stages in flight." />
              : summary.ai_can_handle.slice(0, 5).map((m) => (
                <Link to={`/commandlinc/missions/${m.id}`} key={m.id} className="wide-row row-button" style={{ width: "100%" }}>
                  <div><b>{m.title}</b><p className="hint" style={{ fontSize: 11 }}>stage <span className="mono">{m.current_stage}</span></p></div>
                  <span className="pill blue">running</span>
                </Link>
              ))
            }
          </div>
        </div>

        <div className="panel" data-testid="tile-businesses">
          <TileHeader title="Businesses" Icon={Briefcase} action={<Link to="/businesses" className="btn small">Open</Link>} />
          <div className="panel-body">
            {(summary.businesses || []).map((b) => (
              <Link to={`/businesses/${b.key}`} key={b.key} className="wide-row row-button" style={{ width: "100%" }}>
                <div><b><LincName name={b.name} /></b><p className="hint" style={{ fontSize: 11 }}>{b.status} · {b.business_type?.replace(/_/g, " ")}</p></div>
                <span className={`pill ${HEALTH_TONE[b.health] || "dim"}`}>{b.health}</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="panel" data-testid="tile-upcoming">
          <TileHeader title="Upcoming" Icon={CalendarClock} />
          <div className="panel-body">
            {(summary.upcoming || []).length === 0
              ? <EmptyState title="Nothing due soon." />
              : summary.upcoming.map((u) => (
                <div className="wide-row" key={u.key}>
                  <div><b><LincName name={u.name} /></b><p className="hint" style={{ fontSize: 11 }}>{u.current_milestone || ""}</p></div>
                  <span className="pill dim mono">{u.target_date}</span>
                </div>
              ))
            }
          </div>
        </div>

        <div className="panel" data-testid="tile-waiting-on">
          <TileHeader title="Waiting on" Icon={Clock} />
          <div className="panel-body"><NotConnected>External comms integration not yet wired.</NotConnected></div>
        </div>

        <div className="panel" data-testid="tile-founder-chat">
          <TileHeader title="Founder chat" Icon={MessageSquare} />
          <div className="panel-body"><NotConnected>Realtime voice + chat integration not yet wired.</NotConnected></div>
        </div>
      </div>

      {/* Planning surface */}
      <div className="panel" style={{ marginTop: 16 }} data-testid="focus-planning">
        <div className="panel-head">
          <div>
            <h2><Target size={14} style={{ verticalAlign: -2 }} /> Planning surface</h2>
            <p>Prioritize · park · protect your attention.</p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <small className="hint">Role</small>
            <select className="btn small" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="focus-role-filter">
              <option value="ALL">All</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <div className="panel-body">
          <div className="focus-kind-chips" data-testid="focus-kind-chips">
            {KINDS.map((k) => (
              <button
                key={k.id}
                className={`chip ${kindFilter.includes(k.id) ? "active" : ""}`}
                onClick={() => toggleKind(k.id)}
                data-testid={`kind-${k.id}`}
              >{k.label}</button>
            ))}
            {kindFilter.length ? <button className="chip clear" onClick={() => setKindFilter([])}><X size={10} /> Clear</button> : null}
          </div>

          <div className="focus-buckets">
            {BUCKETS.map((bk) => (
              <div className="focus-bucket" key={bk} data-testid={`focus-bucket-${bk}`}>
                <div className="focus-bucket-head">
                  <b>{BUCKET_LABEL[bk]}</b>
                  <span className="pill dim">{byBucket[bk].length}</span>
                </div>
                <div className="focus-bucket-body">
                  {byBucket[bk].length === 0
                    ? <p className="hint" style={{ fontSize: 12, padding: 8 }}>Empty.</p>
                    : byBucket[bk].map((i) => (
                      <FocusCard key={i.id} item={i} businesses={businesses}
                        onEdit={(x) => setEditing(x)}
                        onMove={requestMove}
                        onStatus={requestStatus}
                        onDelete={doDelete}
                        onPriority={doPriority}
                      />
                    ))
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="focus-grid focus-row-bottom" style={{ marginTop: 14 }}>
        <div className="panel" data-testid="tile-recent-activity">
          <TileHeader title="Recent activity" Icon={Activity} />
          <div className="panel-body">
            {(summary.recent_activity || []).map((e, idx) => (
              <div className="wide-row" key={idx}>
                <div><b className="mono" style={{ fontSize: 12 }}>{e.type}</b><p className="hint" style={{ fontSize: 11 }}>{e.actor_type}</p></div>
                <small className="hint">{fmtDateTime(e.ts)}</small>
              </div>
            ))}
          </div>
        </div>
        <div className="panel" data-testid="tile-cost-summary">
          <TileHeader title="Cost summary" Icon={CircleDollarSign} note="Last 7 days"
            action={<Link to="/commandlinc/cost" className="btn small">Open</Link>} />
          <div className="panel-body">
            <div className="wide-row"><div><b>Spend</b></div><b className="mono">${(cs.total_usd_last_7d || 0).toFixed(4)}</b></div>
            <div className="wide-row"><div><b>Tokens</b></div><span className="mono">{(cs.tokens_last_7d || 0).toLocaleString()}</span></div>
            <div className="wide-row"><div><b>Top department</b></div><span className="pill dim">{cs.top_department || "—"}</span></div>
          </div>
        </div>
        <div className="panel" data-testid="tile-system-health">
          <TileHeader title="System health" Icon={HeartPulse} />
          <div className="panel-body">
            <div className="wide-row"><div><b>Mongo</b></div><span className={`pill ${sh.mongo ? "green" : "red"}`}>{sh.mongo ? "ok" : "down"}</span></div>
            <div className="wide-row"><div><b>LLM provider</b></div><span className={`pill ${sh.llm_provider_reachable ? "green" : "red"}`}>{sh.llm_provider_reachable ? "reachable" : "unreachable"}</span></div>
            <div className="wide-row"><div><b>Seed</b></div><span className={`pill ${sh.seed_ok ? "green" : "red"}`}>{sh.seed_ok ? "ok" : "fail"}</span></div>
          </div>
        </div>
      </div>

      {creating ? (
        <ItemForm mode="create" businesses={businesses} onClose={() => setCreating(false)} onSubmit={doCreate} />
      ) : null}
      {editing ? (
        <ItemForm mode="edit" initial={editing} businesses={businesses} onClose={() => setEditing(null)} onSubmit={doEditSave} />
      ) : null}
      {pendingMove ? (
        <ReasonModal
          title={`Move out of NOW → ${BUCKET_LABEL[pendingMove.to]}?`}
          prompt="You're demoting this item off your immediate attention. Note why so future-you knows."
          testId="move-confirm-modal"
          onClose={() => setPendingMove(null)}
          onSubmit={(reason) => doMove(pendingMove.item, pendingMove.to, reason)}
        />
      ) : null}
      {pendingStatus ? (
        <ReasonModal
          title={`Set status: ${pendingStatus.to}`}
          prompt={pendingStatus.to === "snoozed" ? "Why snooze? (also set a wake-up time in the item's editor)" : "Reason?"}
          testId="status-confirm-modal"
          onClose={() => setPendingStatus(null)}
          onSubmit={(reason) => {
            const snoozed_until = pendingStatus.to === "snoozed" ? new Date(Date.now() + 24 * 3600 * 1000).toISOString() : null;
            doStatus(pendingStatus.item, pendingStatus.to, reason, snoozed_until);
          }}
        />
      ) : null}
    </div>
  );
}
